import { resourceCatalog } from 'core/types/generated/resources'

import type { ResourceCatalog } from './types'

// The API calls a borrower a person; the UI calls it a borrower.
const ALIASES: { [word: string]: string } = {
  borrower: 'people',
  person: 'people',
}

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z]/g, '')

const indexCache = new WeakMap<object, { [normalized: string]: string }>()

/**
 * Normalized catalog key to key, built once per catalog. Entries without a
 * single-resource read are omitted, so a noun with no detail page can never
 * become a link target.
 */
const indexOf = (
  catalog: ResourceCatalog,
): { [normalized: string]: string } => {
  const cached = indexCache.get(catalog)
  if (cached) return cached

  const index: { [normalized: string]: string } = {}
  for (const [key, entry] of Object.entries(catalog)) {
    if (!entry.operations?.get || !entry.idParam) continue
    index[normalize(key)] = key
  }
  indexCache.set(catalog, index)
  return index
}

const candidates = (word: string): Array<string> => {
  const alias = ALIASES[word.toLowerCase()]
  return [
    alias,
    word,
    `${word}s`,
    `${word}es`,
    word.replace(/y$/, 'ies'),
  ].filter((candidate): candidate is string => !!candidate)
}

/**
 * Resolves a field name to a catalog key by trying the longest camel-case word
 * suffix first, so `escalatedEmployeeId` finds `employees` while
 * `loanExternalId` finds nothing.
 */
export const matchCatalog = (
  fieldName: string,
  catalog: ResourceCatalog = resourceCatalog,
): string | null => {
  if (!fieldName.endsWith('Id')) return null

  const base = fieldName.slice(0, -2)
  if (!base) return null

  const words = base.split(/(?=[A-Z])/)
  const index = indexOf(catalog)

  for (let start = 0; start < words.length; start++) {
    const joined = words.slice(start).join('')
    const camel = joined.charAt(0).toLowerCase() + joined.slice(1)
    for (const candidate of candidates(camel)) {
      const hit = index[normalize(candidate)]
      if (hit) return hit
    }
  }

  return null
}
